import React, { useEffect, useRef, useState } from "react";

import { Form } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";

import { Participant, ParticipantTypes, useSearchParticipantsQuery } from "@api/graphql";
import { isCollaboration } from "@util/index";

import styles from "./ParticipantSearchField.module.scss";

interface ParticipantSearchFieldProps {
  id: string;
  label: string;
  placeholder: string;
  labelKey: keyof Participant;
  onChange: (selected: Participant | undefined) => void;
  selected?: Participant[];
  selectedParticipantIds?: Set<number>;
  currentParticipantTypes?: ParticipantTypes[];
  allowDeceased?: boolean;
}

export const ParticipantSearchField = ({
  id,
  label,
  placeholder,
  labelKey,
  onChange,
  selected: propSelected,
  selectedParticipantIds,
  currentParticipantTypes,
  allowDeceased = false,
}: ParticipantSearchFieldProps) => {
  const [query, setQuery] = useState<string>("");
  const [selected, setSelected] = useState<Participant[]>(propSelected || []);
  const prevPropSelected = useRef<Participant[] | undefined>(propSelected);

  const participantTypeFilter = isCollaboration(currentParticipantTypes)
    ? ["ARTIST"] // Collaboration accounts can only add artists
    : ["BENEFICIARY", "ARTIST"];

  const baseFilters = [
    { field: "participantType", in: participantTypeFilter },
    { field: "partialName", iLike: query },
  ];

  const filters = !allowDeceased
    ? [
        ...baseFilters,
        {
          field: "deceased",
          OR: [
            { field: "deceased", eq: "false" },
            { field: "deceased", eq: null! },
          ],
        },
      ]
    : baseFilters;

  const { data, loading } = useSearchParticipantsQuery({
    variables: {
      filter_by: filters,
    },
    skip: !query,
  });

  const options = data?.searchParticipants || [];

  const handleSearch = (query: string) => {
    setQuery(query);
  };

  const handleChange = (participants: Participant[]) => {
    if (participants.length > 0) {
      const selectedParticipant = participants[participants.length - 1];
      setSelected([selectedParticipant]); // Update the selected state
      onChange(selectedParticipant); // Notify parent about the change
    } else {
      setSelected([]); // Clear the selected state
      onChange(undefined); // Notify parent that selection is cleared
    }
  };
  // Reset selected when propSelected changes
  useEffect(() => {
    if (prevPropSelected.current !== propSelected) {
      setSelected(propSelected || []);
      prevPropSelected.current = propSelected;
    }
  }, [propSelected]);

  // Filter out participants already selected to prevent duplicates
  const filteredOptions =
    options.length === 0
      ? [
          {
            participant_id: -1,
            full_name: query,
            participant_types: [ParticipantTypes.BENEFICIARY],
          } as Participant,
        ]
      : options.filter((participant) => !selectedParticipantIds?.has(participant.participant_id));

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <AsyncTypeahead
        id={id}
        filterBy={() => true}
        emptyLabel="No Participant Found"
        isLoading={loading}
        labelKey={labelKey as string}
        minLength={3}
        onSearch={handleSearch}
        options={filteredOptions}
        selected={selected}
        placeholder={placeholder}
        onChange={(selected) => handleChange(selected as Participant[])}
        renderMenuItemChildren={(option) => {
          const participant = option as Participant;
          return <span>{participant.full_name}</span>;
        }}
        className={styles["rbt-input-main"]}
      />
    </Form.Group>
  );
};
